import { useRef, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// API's
import { recalculateWashout } from "@/Washouts/api/common/mutations";

// Helpers
import { handleWashoutSubscriptions } from "@/Washouts/helpers/washoutSubscriptions";
import useApproveRetractWashoutSubscription from "@/Washouts/hooks/WashoutReview/useApproveRetractWashoutSubscription";
import useApproveAllWashoutSubscription from "@/Washouts/hooks/common/useApproveAllWashoutSubscription";

// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";
import { useFactoryWashout } from "@/Washouts/hooks/useFactoryWashout";
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";

// Components
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import SalesAssociateWashoutContent from "./SalesAssociateWashoutContent";
import UpdatedOn from "@/Washouts/components/UpdatedOn";

// Images
import empty from "@/assets/images/commons/empty.png";
import emptyTable from "@/assets/images/commons/empty_table.png";

// Constants
import actionsList from "@/Constants/actionsList";

const positionLabel = "Sales Associate";

export default function SalesAssociateWashoutContainer(props) {
	const { location, selected, updateSidePanelCallBack, activeCommissionCycles = [] } = props;

	const queryClient = useQueryClient();
	const { useWashoutQueries } = useFactoryWashout();
	const washoutQueries = useWashoutQueries("Sales Associate");
	const { getSubscription } = useSubscriptions();

	const { data: model, isLoading } = useQuery({
		queryKey: [
			"getWashout",
			props.selected?.payPeriodId,
			props.selected?.position,
			props.selected?.userId,
			props.selected?.commissionCycleId,
		],
		queryFn: async () => {
			const { locationId } = props.location || {};
			const { userId, commissionCycleId, payPeriodId, position } = props.selected || {};
			return washoutQueries?.getWashout(locationId, userId, commissionCycleId, payPeriodId, position);
		},

		enabled: !!props.location && !!props.selected,
		staleTime: Infinity,
	});

	const { mutate: recalculate } = useMutation({
		mutationKey: ["recalculateWashout"],
		mutationFn: (params) => recalculateWashout(params),
	});

	useApproveRetractWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
		updateWashoutModel: true,
	});

	useApproveAllWashoutSubscription({
		locationId: props.location?.locationId,
		commissionCycleId: props.selected?.commissionCycleId,
		payPeriodId: props.selected?.payPeriodId,
	});

	const subscriptionsInformation = props?.subscriptionsInformation;

	const { useAuth } = useAuthorization();
	const overrideCompensation = useAuth(actionsList.OVERRIDE_SALES_ASSOCIATE_COMPENSATION);

	const handleSummaryChange = (input) => {
		if (input && props.location) {
			const params = {
				input,
				position: model?.employee?.position || positionLabel,
				isDepartmental: false,
				locationId: props.location.locationId,
				washoutId: model._id,
			};
			recalculate(params);
		}
	};

	useEffect(() => {
		if (!location?.locationId || !selected) {
			return;
		}

		handleWashoutSubscriptions({
			subscriptionsInformation,
			props: {
				location,
				activeCommissionCycles,
				selected,
				positionWithoutSpaces: "salesAssociates",
			},
			actions: {
				queryClient,
				getSubscription,
			},
			options: {
				useRanking: true,
			},
		});
	}, [
		subscriptionsInformation,
		selected?.userId,
		selected?.commissionCycleId,
		selected?.payPeriodId,
		location?.locationId,
		queryClient,
		getSubscription,
		activeCommissionCycles,
	]);

	// Only call updateSidePanelCallBack when selection changes
	const prevSelectedRef = useRef(selected);
	useEffect(() => {
		if (!selected) {
			return;
		}
		if (prevSelectedRef.current !== selected) {
			updateSidePanelCallBack?.(selected);
			prevSelectedRef.current = selected;
		}
	}, [selected, updateSidePanelCallBack]);

	return (
		<>
			<SalesAssociateWashoutContent
				model={model}
				locationId={props.location.locationId}
				isLoading={isLoading}
				overrideCompensation={overrideCompensation}
				selected={props.selected}
				handleSummaryChange={handleSummaryChange}
			/>

			{!model && !isLoading && (
				<RoundedBox>
					<EmptyBody
						image={props.selected ? emptyTable : empty}
						title={props.selected ? "No washout found for this associate" : "Select an associate to view their washout"}
						bodyHeight={"510px"}
						sx={{
							boxShadow: "none !important",
						}}
					/>
				</RoundedBox>
			)}

			<UpdatedOn isLoading={isLoading} updatedOn={model?.created} />
		</>
	);
}

import PropTypes from "prop-types";
SalesAssociateWashoutContainer.propTypes = {
	model: PropTypes.shape({
		productConversions: PropTypes.array,
		callActivityPerformance: PropTypes.array,
	}),
	location: PropTypes.shape({
		locationId: PropTypes.string,
	}),
	selected: PropTypes.shape({
		userId: PropTypes.string,
		commissionCycleId: PropTypes.string,
		payPeriodId: PropTypes.string,
		status: PropTypes.string,
		position: PropTypes.string,
	}),
	subscriptionsInformation: PropTypes.object,
	updateSidePanelCallBack: PropTypes.func,
	activeCommissionCycles: PropTypes.array,
};
